import { InfoOutlineIcon } from '@carboncopyco/icons';
import classNames from 'classnames';
import type React from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ColorHex } from '../../../constants';
import { Tooltip } from '../../Tooltip';
import { TooltipMessage } from '../../TooltipMessage';
import { TextCaption } from '../../Typography';

const INPUT_TYPE = 'text';
export type InputSizes = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl';
export type InputMultilineHeight = 'sm' | 'md' | 'lg';
export type InputState =
  | 'normal'
  | 'success'
  | 'error'
  | 'warning'
  | 'invisible'
  | 'invisible2'
  | 'dark';

declare module 'react' {
  interface InputHTMLAttributes<T> {
    dataDsId?: string;
  }
}

export interface InputTextProps {
  fullWidth?: boolean;
  className?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyUp?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyPress?: (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  placeholder?: string;
  name?: string;
  value?: string;
  disabled?: boolean;
  state?: InputState;
  size?: InputSizes;
  defaultValue?: string;
  multiline?: boolean;
  multilineOptions?: {
    height?: InputMultilineHeight;
    isExpandableHeight?: boolean;
  };
  autoFocus?: boolean;
  dataDsId?: string;
  password?: boolean;
  rightElement?: React.ReactNode;
  leftElement?: React.ReactNode;
  showCharsCounter?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  ellipsis?: boolean;
  tabIndex?: number;
  errorTooltipMessage?: string;
}

const states = {
  normal: 'bg-white text-ds-natural-600 outline-ds-natural-100',
  warning: 'bg-white text-ds-natural-600 outline-ds-secondary-400',
  success: 'bg-white text-ds-natural-600 outline-ds-accent2-400',
  error: 'bg-white text-ds-natural-600 outline-ds-danger-400',
  invisible: 'outline-none',
  dark: 'outline-ds-natural-150',
  invisible2: 'outline-none',
};

const disabledState =
  'bg-ds-natural-200-a text-ds-natural-200 outline-ds-natural-200';

const focusState = {
  normal: 'focus:outline-ds-primary-300 focus-within:outline-ds-primary-300',
  warning:
    'focus:outline-ds-secondary-400 focus-within:outline-ds-secondary-400',
  success: 'focus:outline-ds-primary-300 focus-within:outline-ds-primary-300',
  error: 'focus:outline-ds-danger-400 focus-within:outline-ds-danger-400',
  invisible2:
    'focus:outline-ds-primary-300 focus-within:outline-ds-primary-300',
};

const hoverState = {
  normal: 'hover:outline-ds-natural-300',
  invisible2: 'hover:outline-ds-natural-300',
};

const focusAndHover = {
  normal:
    'hover:focus:outline-ds-primary-300 hover:focus-within:outline-ds-primary-300',
  invisible2:
    'hover:focus:outline-ds-primary-300 hover:focus-within:outline-ds-primary-300',
};

const sizes = {
  sm: 'px-2.5 py-1 text-ds-400 font-ds-normal',
  md: 'px-3 py-1 text-ds-400 font-ds-normal',
  lg: 'px-3 py-2 text-ds-400 font-ds-normal',
  xl: 'text-ds-500 leading-ds-400 font-ds-normal px-2',
  xxl: 'text-ds-800 font-ds-medium leading-ds-700 py-0 px-1',
  xxxl: 'text-ds-800 font-ds-medium leading-ds-700 py-0 px-1',
  header: 'p-0 text-ds-800 font-ds-medium',
};

const heights = {
  sm: 'h-ds-sm-input',
  md: 'h-ds-md-input',
  lg: 'h-ds-lg-input',
  xl: 'h-ds-lg-input',
  xxxl: 'h-12',
};

const heightsInPX = {
  sm: 24,
  md: 30,
  lg: 40,
  xl: 40,
};

const multilineHeights = {
  sm: {
    className: 'h-ds-18',
    height: 72,
  },
  md: {
    className: 'h-44',
    height: 176,
  },
  lg: {
    className: 'h-[340px]',
    height: 340,
  },
};

const multilineDefaultOptions = {
  height: 'sm',
  isExpandableHeight: false,
};

export const inputClasses = `
  rounded-md
  placeholder-ds-natural-300
  leading-ds-300
  box-border
  outline
  !outline-1
  outline-offset-[-1px]
  tracking-ds-normal
  font-ds-inter
  `;

export const InputText = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputTextProps
>(
  (
    {
      fullWidth = false,
      state = 'normal',
      size = 'md',
      onChange,
      placeholder,
      name,
      value,
      disabled,
      defaultValue,
      multiline,
      multilineOptions = multilineDefaultOptions,
      onFocus,
      onBlur,
      autoFocus,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      dataDsId,
      password,
      rightElement,
      leftElement,
      showCharsCounter = false,
      maxLength,
      readOnly,
      ellipsis,
      tabIndex,
      errorTooltipMessage,
    },
    ref
  ) => {
    const tooltipRef = useRef<HTMLDivElement>();
    const [inputValue, setInputValue] = useState<string>(value as string);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const { height, isExpandableHeight } = {
      ...multilineDefaultOptions,
      ...multilineOptions,
    };

    const setTextAreaHeightToMinContent = (
      textAreaElement: HTMLTextAreaElement,
      scrollHeight: number,
      maxHeight: number
    ): void => {
      if (scrollHeight > 0) {
        textAreaElement.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      }
    };

    useImperativeHandle(
      ref,
      () => inputRef.current as HTMLInputElement | HTMLTextAreaElement
    );

    useEffect(() => {
      setInputValue(value as string);
    }, [value]);

    useEffect(() => {
      if (multiline) {
        const maxHeight = multilineHeights[height].height;
        const textAreaElement = inputRef?.current as HTMLTextAreaElement;
        if (textAreaElement && isExpandableHeight) {
          textAreaElement.style.height = '0px';
          const scrollHeight = Math.max(
            textAreaElement.scrollHeight,
            heightsInPX[size]
          );
          setTextAreaHeightToMinContent(
            textAreaElement,
            scrollHeight,
            maxHeight
          );
        }
      }
    }, [inputValue, multiline, height, isExpandableHeight]);

    const handleChange = (
      event: React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
      const val = event.target?.value;
      onChange?.(event);
      setInputValue(val);
    };

    const focusHandler = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (readOnly && ellipsis) {
        e.target.blur();
      }

      onFocus?.(e);
    };

    return multiline ? (
      <>
        <textarea
          autoComplete="off"
          disabled={disabled}
          ref={inputRef as React.Ref<HTMLTextAreaElement>}
          onFocus={focusHandler}
          autoFocus={autoFocus}
          maxLength={maxLength}
          name={name}
          value={inputValue}
          defaultValue={defaultValue}
          onChange={handleChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          data-ds-id={dataDsId}
          id={name}
          rows={1}
          readOnly={readOnly}
          className={classNames(
            inputClasses,
            'resize-none border-none focus:ring-0',
            states[state],
            !readOnly && !disabled && hoverState[state],
            !readOnly && !disabled && focusState[state],
            !readOnly && !disabled && focusAndHover[state],
            sizes[size],
            !isExpandableHeight ? multilineHeights[height].className : '',
            state === 'dark'
              ? 'text-ds-natural-50 bg-transparent focus:border-ds-accent3-100'
              : 'text-ds-natural-600 bg-ds-accent3-100 focus:border-ds-primary',
            {
              'w-full': fullWidth,
            }
          )}
        />
        {showCharsCounter && maxLength && (
          <div
            className={classNames('absolute flex items-center right-0', {
              'top-[72px]': height === 'sm',
              'top-[176px]': height === 'md',
              'top-[340px]': height === 'lg',
            })}
          >
            <div>
              <TextCaption
                label={`${inputValue?.length}/${maxLength}`}
                color={
                  inputValue?.length === maxLength
                    ? 'text-ds-natural-600'
                    : 'text-ds-natural-300'
                }
              />
            </div>
          </div>
        )}
      </>
    ) : (
      <div
        className={classNames(
          'flex items-center justify-between gap-1',
          inputClasses,
          disabled ? disabledState : states[state],
          !readOnly && !disabled && hoverState[state],
          !readOnly && !disabled && focusState[state],
          !readOnly && !disabled && focusAndHover[state],
          sizes[size],
          heights[size],
          {
            'w-full': fullWidth,
          }
        )}
        tabIndex={tabIndex || 0}
        onFocus={() => inputRef.current?.focus()}
      >
        {leftElement && (
          <div className="flex items-center h-full">{leftElement}</div>
        )}
        <input
          onBlur={onBlur}
          onFocus={focusHandler}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onKeyPress={onKeyPress}
          autoComplete="off"
          type={password ? 'password' : INPUT_TYPE}
          disabled={disabled}
          ref={inputRef as React.Ref<HTMLInputElement>}
          autoFocus={autoFocus}
          name={name}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          data-ds-id={dataDsId}
          placeholder={placeholder}
          id={name}
          readOnly={readOnly}
          style={{
            cursor: 'inherit',
            fontSize: 'inherit',
          }}
          className={classNames(
            'outline-none border-none p-0 focus:ring-0 flex-1 h-full bg-transparent w-full',
            {
              'text-ellipsis': ellipsis,
            }
          )}
        />
        {(rightElement || (errorTooltipMessage && state === 'error')) && (
          <div
            className="flex items-center h-full"
            data-ds-id="right-element"
          >
            {rightElement ? (
              rightElement
            ) : (
              <>
                <div
                  role="button"
                  ref={tooltipRef}
                  className="ml-2"
                  aria-label="Show more info"
                >
                  <InfoOutlineIcon
                    size={20}
                    color={ColorHex.danger}
                  />
                </div>
                <Tooltip
                  shadow="md"
                  noArrow
                  placement="bottom"
                  anchorRef={tooltipRef}
                >
                  <TooltipMessage
                    title={errorTooltipMessage}
                    wrapMessage={true}
                  />
                </Tooltip>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

InputText.displayName = 'InputText';
